import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { Space, Tag, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import { type Key } from 'antd/es/table/interface';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import { getTagByRelativeDate } from '../fragments/get-tag-by-relative-date';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { marketingConsentCampaignSentList } from '../reducers/marketing-consent-campaign/reducer';
import { type IMarketingConsentCampaignSentCustomer } from '../reducers/marketing-consent-campaign/types';
import { SPACING } from '../theme/tokens';

interface DataType extends IMarketingConsentCampaignSentCustomer {
  key: string;
}

function renderResponse(record: DataType): React.ReactElement {
  if (!record.respondedViaThisCampaign) {
    return <Tag color="orange">Pending</Tag>;
  }

  return record.currentConsent ? (
    <Tag color="green">Yes</Tag>
  ) : (
    <Tag color="default">No</Tag>
  );
}

function MarketingConsentCampaignSentContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const sentCustomers = useSelector(
    (s: RootState) => s.MarketingConsentCampaign.sentCustomers,
  );
  const sentSummary = useSelector(
    (s: RootState) => s.MarketingConsentCampaign.sentSummary,
  );
  const inProcess = useSelector(
    (s: RootState) => s.MarketingConsentCampaign.inProcess,
  );

  const [dataSource, setDataSource] = useState<DataType[]>([]);

  useEffect((): void => {
    dispatch(marketingConsentCampaignSentList());
  }, [dispatch]);

  useEffect((): void => {
    setDataSource(
      sentCustomers?.map(
        (c: IMarketingConsentCampaignSentCustomer): DataType => ({
          ...c,
          key: c.uuid,
        }),
      ) ?? [],
    );
  }, [sentCustomers]);

  const columns: ColumnsType = [
    {
      title: 'Email',
      dataIndex: 'email',
      render: (
        email: string | undefined,
        record: DataType,
      ): React.ReactElement => (
        <Link
          to={`/crm/customer/${record.uuid}`}
          label={email ?? record.uuid}
          openInNewTab
        />
      ),
      sorter: (a: DataType, b: DataType): number =>
        stringCompare(a.email ?? '', b.email ?? ''),
    },
    {
      title: 'Sent At',
      dataIndex: 'sentAt',
      sorter: (a: DataType, b: DataType): number =>
        new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime(),
      defaultSortOrder: 'descend',
      render: (sentAt: string): React.ReactElement =>
        getTagByRelativeDate({ date: new Date(sentAt) }),
    },
    {
      title: 'Response',
      key: 'response',
      filters: [
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' },
        { text: 'Pending', value: 'pending' },
      ],
      onFilter: (value: boolean | Key, record: DataType): boolean => {
        if (!record.respondedViaThisCampaign) {
          return value === 'pending';
        }

        return value === (record.currentConsent ? 'yes' : 'no');
      },
      render: (_: unknown, record: DataType): React.ReactElement =>
        renderResponse(record),
    },
  ];

  return (
    <Space orientation="vertical" size={SPACING.lg} style={{ width: '100%' }}>
      <DetailPageHeader
        title="Marketing consent — sent history"
        onReload={(): void => {
          dispatch(marketingConsentCampaignSentList());
        }}
        isReloadDisabled={inProcess}
        tags={
          sentSummary && (
            <>
              <Tag color="green">{`Yes: ${sentSummary.yesCount}`}</Tag>
              <Tag color="default">{`No: ${sentSummary.noCount}`}</Tag>
              <Tag color="orange">{`Pending: ${sentSummary.pendingCount}`}</Tag>
            </>
          )
        }
        info={
          sentCustomers !== undefined && (
            <Typography.Text type="secondary">
              {`${sentCustomers.length} sent total`}
            </Typography.Text>
          )
        }
      />

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={inProcess && !sentCustomers}
        scroll={{ x: true }}
      />
    </Space>
  );
}

export default MarketingConsentCampaignSentContainer;
